import * as THREE from 'three';
import { BasketballDimensions } from '@shared/constants/BasketballDimensions';
import { DebugTools } from '../utils/DebugTools';
import { eventBus } from '../events/EventBus';
import { Debug, LogCategory, LogLevel } from '@shared/Debug';

/**
 * Manages debug visualizations and tools
 */
export class DebugManager {
  private scene: THREE.Scene;
  
  // Debug visualization elements
  private coordinateAxes: THREE.Group | null = null;
  private gridHelper: THREE.GridHelper | null = null;
  private showDebugVisualization: boolean = false;
  
  // Rigid body visualization
  private rigidBodies: Map<string, THREE.Mesh> = new Map();
  private showRigidBodies: boolean = false;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    
    // Set up event listeners
    this.initializeEventListeners();
  }

  private initializeEventListeners(): void {
    // Listen for debug toggle events
    eventBus.subscribe('debug:toggle-visualizations', (visible?: boolean) => {
      this.toggleDebugVisualizations(visible);
    });
    
    eventBus.subscribe('debug:toggle-rigid-bodies', (visible?: boolean) => {
      this.toggleDebugMeshes(visible);
    });
  }

  /**
   * Initialize debug visualization elements
   */
  public initialize(): void {
    if (BasketballDimensions.debug.showAxes) {
      // Create coordinate axes
      this.coordinateAxes = DebugTools.createCoordinateAxes();
      this.scene.add(this.coordinateAxes);
      
      // Create a ground grid helper
      this.gridHelper = DebugTools.createGridHelper();
      this.scene.add(this.gridHelper);
      
      // Set initial visibility based on debug setting - explicitly set to false
      this.toggleDebugVisualizations(false);
    }
    
    Debug.info('Debug manager initialized', LogCategory.GENERAL);
    Debug.setLogLevel(LogLevel.WARNING);
  }

  /**
   * Toggle the visibility of debug visualization elements
   */
  public toggleDebugVisualizations(visible?: boolean): void {
    // If visible is not provided, toggle current state
    if (visible === undefined) {
      this.showDebugVisualization = !this.showDebugVisualization;
    } else {
      this.showDebugVisualization = visible;
    }
    
    // Update visualization elements
    if (this.coordinateAxes) {
      this.coordinateAxes.visible = this.showDebugVisualization;
    }
    
    if (this.gridHelper) {
      this.gridHelper.visible = this.showDebugVisualization;
    }
    
    Debug.info(`Debug visualizations ${this.showDebugVisualization ? 'enabled' : 'disabled'}`, LogCategory.GENERAL);
  }

  /**
   * Toggle the visibility of debug meshes (rigid bodies)
   */
  public toggleDebugMeshes(visible?: boolean): void {
    // If visible is not provided, toggle current state
    if (visible === undefined) {
      this.showRigidBodies = !this.showRigidBodies;
    } else {
      this.showRigidBodies = visible;
    }
    
    // Update visibility for all rigid body meshes
    this.rigidBodies.forEach(mesh => {
      mesh.visible = this.showRigidBodies;
    });
    
    Debug.info(`Debug meshes are now ${this.showRigidBodies ? 'visible' : 'hidden'}`, LogCategory.GENERAL);
  }

  /**
   * Update rigid bodies based on server data
   */
  public updateRigidBodies(rigidBodiesData: any): void {
    if (!rigidBodiesData) return;
    
    // Track the IDs we've seen in this update
    const seenIds = new Set<string>();
    
    // Update or create rigid bodies
    rigidBodiesData.forEach((bodyData: any, id: string) => {
      seenIds.add(id);
      
      // If we don't have this rigid body yet, create it
      if (!this.rigidBodies.has(id)) {
        this.createRigidBodyMesh(id, bodyData);
      }
      
      // Update the existing rigid body
      const rigidBodyMesh = this.rigidBodies.get(id);
      if (rigidBodyMesh) {
        rigidBodyMesh.visible = this.showRigidBodies;
        // Update position
        rigidBodyMesh.position.set(
          bodyData.x || 0,
          bodyData.y || 0,
          bodyData.z || 0
        );
        
        // Update rotation if provided
        if (bodyData.quaternion) {
          rigidBodyMesh.quaternion.set(
            bodyData.quaternion.x || 0,
            bodyData.quaternion.y || 0,
            bodyData.quaternion.z || 0,
            bodyData.quaternion.w || 1
          );
        }
      }
    });
    
    // Remove any rigid bodies that weren't in this update
    this.rigidBodies.forEach((mesh, id) => {
      if (!seenIds.has(id)) {
        this.scene.remove(mesh);
        this.rigidBodies.delete(id);
      }
    });
  }

  /**
   * Create a translucent mesh for a rigid body
   */
  private createRigidBodyMesh(id: string, bodyData: any): void {
    let geometry: THREE.BufferGeometry;
    
    // Create geometry based on shape type
    switch (bodyData.type) {
      case 'box':
        geometry = new THREE.BoxGeometry(
          bodyData.width || 1,
          bodyData.height || 1,
          bodyData.depth || 1
        );
        break;
      case 'sphere':
        geometry = new THREE.SphereGeometry(
          bodyData.radius || 0.5,
          32,
          32
        );
        break;
      case 'cylinder':
        geometry = new THREE.CylinderGeometry(
          bodyData.radiusTop || 0.5,
          bodyData.radiusBottom || 0.5,
          bodyData.height || 1,
          32
        );
        break;
      default:
        // Default to a small cube if shape is not recognized
        geometry = new THREE.BoxGeometry(0.5, 0.5, 0.5);
    }
    
    // Create a translucent material with a color
    const color = bodyData.color;
    const material = new THREE.MeshBasicMaterial({
      color: color,
      transparent: true,
      opacity: bodyData.id?.includes('rim') ? 0.7 : 0.4, // Higher opacity for rims
      wireframe: bodyData.wireframe || false
    });
    
    // Create the mesh
    const mesh = new THREE.Mesh(geometry, material);
    
    // Set initial position
    mesh.position.set(
      bodyData.x || 0,
      bodyData.y || 0,
      bodyData.z || 0
    );
    
    // Set initial rotation
    mesh.rotation.x = bodyData.rotationX || 0;
    mesh.rotation.y = bodyData.rotationY || 0;
    mesh.rotation.z = bodyData.rotationZ || 0;
    
    // Set initial quaternion if provided
    if (bodyData.quaternion) {
      mesh.quaternion.set(
        bodyData.quaternion.x || 0,
        bodyData.quaternion.y || 0,
        bodyData.quaternion.z || 0,
        bodyData.quaternion.w || 1
      );
    }
    
    // Add to scene and store in map
    this.scene.add(mesh);
    this.rigidBodies.set(id, mesh);
    
    // Set initial visibility
    mesh.visible = this.showRigidBodies;
  }

  /**
   * Get a rigid body mesh by ID
   */
  public getRigidBody(id: string): THREE.Mesh | undefined {
    return this.rigidBodies.get(id);
  }

  /**
   * Cleanup resources
   */
  public dispose(): void {
    // Remove debug visualization elements
    if (this.coordinateAxes) {
      this.scene.remove(this.coordinateAxes);
      this.coordinateAxes = null;
    }
    
    if (this.gridHelper) {
      this.scene.remove(this.gridHelper);
      this.gridHelper = null;
    }
    
    // Remove all rigid body meshes
    this.rigidBodies.forEach(mesh => {
      this.scene.remove(mesh);
    });
    this.rigidBodies.clear();
  }
}